import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link, useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useMetaTags} from '../../services/useMetaTags';
import {useAuth} from '../../services/use-auth';
import AdvertiseBlock from '../../components/AdvertiseBlock';
import trackEvent from '../../services/trackEvent';
import trackPageView from '../../services/trackPageView';
import NotFoundPage from '../Errors/NotFound';
import '../../assets/styles/library/libraryBrowser.scss';

const PER_PAGE = 15;

function normalizeSortValue(value) {
    const normalized = String(value || '').trim();

    if (['relevance', 'newest', 'popular'].includes(normalized)) {
        return normalized;
    }

    return 'relevance';
}

function parsePositiveInt(value, fallback = 1) {
    const parsed = Number.parseInt(String(value || ''), 10);

    if (!Number.isFinite(parsed) || parsed < 1) {
        return fallback;
    }

    return parsed;
}

function parseUniqueIntegerArray(values) {
    const unique = new Set();

    values.forEach((value) => {
        const parsed = Number.parseInt(String(value || ''), 10);
        if (Number.isInteger(parsed) && parsed > 0) {
            unique.add(parsed);
        }
    });

    return Array.from(unique);
}

function parseUniqueStringArray(values) {
    const unique = new Set();

    values.forEach((value) => {
        const normalized = String(value || '').trim().toLowerCase();
        if (normalized !== '') {
            unique.add(normalized);
        }
    });

    return Array.from(unique);
}

function getAllQueryValues(params, key) {
    return [
        ...params.getAll(key),
        ...params.getAll(`${key}[]`),
    ];
}

function parseQueryState(search, routePage, allowSectionFilter) {
    const params = new URLSearchParams(search || '');
    const searchValue = params.get('search') || '';
    const hasExplicitSort = params.has('sort');
    const sortValue = hasExplicitSort
        ? normalizeSortValue(params.get('sort'))
        : (searchValue.trim() === '' ? 'newest' : 'relevance');

    return {
        search: searchValue,
        sort: sortValue,
        sectionIds: allowSectionFilter ? parseUniqueIntegerArray(getAllQueryValues(params, 'sections')) : [],
        tags: parseUniqueStringArray(getAllQueryValues(params, 'tags')),
        authorId: String(parsePositiveInt(params.get('author') || '', 0) || ''),
        dateFrom: params.get('dateFrom') || '',
        dateTo: params.get('dateTo') || '',
        page: allowSectionFilter ? parsePositiveInt(params.get('page') || '1', 1) : routePage,
    };
}

function buildBrowserQuery(state, {allowSectionFilter, includePage}) {
    const params = new URLSearchParams();

    if ((state.search || '').trim() !== '') {
        params.set('search', state.search.trim());
    }

    if (state.sort && state.sort !== 'relevance') {
        params.set('sort', state.sort);
    }

    if (allowSectionFilter) {
        (state.sectionIds || []).forEach((value) => {
            params.append('sections[]', String(value));
        });
    }

    (state.tags || []).forEach((tag) => {
        params.append('tags[]', tag);
    });

    if ((state.authorId || '').trim() !== '') {
        params.set('author', String(state.authorId).trim());
    }

    if (state.dateFrom) {
        params.set('dateFrom', state.dateFrom);
    }

    if (state.dateTo) {
        params.set('dateTo', state.dateTo);
    }

    if (includePage && (state.page || 1) > 1) {
        params.set('page', String(state.page));
    }

    return params.toString();
}

function buildApiQuery(state, {allowSectionFilter, sectionId}) {
    const params = new URLSearchParams();
    params.set('page', String(state.page || 1));

    if (sectionId) {
        params.set('sectionId', String(sectionId));
    }

    if ((state.search || '').trim() !== '') {
        params.set('search', state.search.trim());
    }

    if (state.sort && state.sort !== 'relevance') {
        params.set('sort', state.sort);
    }

    if (allowSectionFilter) {
        (state.sectionIds || []).forEach((value) => {
            params.append('sections[]', String(value));
        });
    }

    (state.tags || []).forEach((tag) => {
        params.append('tags[]', tag);
    });

    if ((state.authorId || '').trim() !== '') {
        params.set('author', String(state.authorId).trim());
    }

    if (state.dateFrom) {
        params.set('dateFrom', state.dateFrom);
    }

    if (state.dateTo) {
        params.set('dateTo', state.dateTo);
    }

    return params.toString();
}

function buildLibraryPath(sectionId, page) {
    if (!sectionId) {
        return '/library';
    }

    if ((page || 1) <= 1) {
        return `/library/${sectionId}`;
    }

    return `/library/${sectionId}/${page}`;
}

function getPaginationItems(currentPage, totalPages) {
    if (totalPages <= 7) {
        return Array.from({length: totalPages}, (_, index) => index + 1);
    }

    const items = [1];
    const from = Math.max(2, currentPage - 1);
    const to = Math.min(totalPages - 1, currentPage + 1);

    if (from > 2) {
        items.push('ellipsis-left');
    }

    for (let page = from; page <= to; page += 1) {
        items.push(page);
    }

    if (to < totalPages - 1) {
        items.push('ellipsis-right');
    }

    items.push(totalPages);

    return items;
}

function normalizeUrl(value) {
    if (!value) {
        return '/img/fallback-min.png';
    }

    if (/^(https?:)?\/\//.test(value)) {
        return value;
    }

    return value.startsWith('/') ? value : `/${value}`;
}

function formatNumericValue(value) {
    const numeric = Number(value);

    if (!Number.isFinite(numeric)) {
        return '';
    }

    if (Number.isInteger(numeric)) {
        return String(numeric);
    }

    return String(numeric)
        .replace(/\.0+$/, '')
        .replace(/(\.\d*[1-9])0+$/, '$1');
}

function formatRewardLabel(points, t) {
    const items = Array.isArray(points) ? points : [];
    const parts = items
        .map((point) => {
            const code = String(point?.currencyCode || point?.currency?.code || '')
                .trim()
                .toLowerCase();
            const amount = formatNumericValue(point?.price ?? point?.value ?? point?.amount);

            if (!code || !amount) {
                return null;
            }

            return `${amount} ${t(`app.currencies.${code}`, code.toUpperCase())}`;
        })
        .filter(Boolean);

    return parts.join(' / ');
}

function formatPriceLabel(priceOptions, t) {
    const options = Array.isArray(priceOptions) ? priceOptions : [];
    const parts = options
        .map((option) => {
            const code = String(option?.currencyCode || '').trim().toLowerCase();
            const amount = formatNumericValue(option?.amount ?? option?.price ?? option?.value);

            if (!code || !amount) {
                return null;
            }

            return `${amount} ${t(`app.currencies.${code}`, code.toUpperCase())}`;
        })
        .filter(Boolean);

    if (parts.length === 0) {
        return t('courses.list.chips.free', 'Free');
    }

    return parts.join(' / ');
}

function hasPaidPrice(priceOptions) {
    const options = Array.isArray(priceOptions) ? priceOptions : [];

    return options.some((option) => {
        const code = String(option?.currencyCode || '').trim().toLowerCase();
        const amount = formatNumericValue(option?.amount ?? option?.price ?? option?.value);

        return Boolean(code && amount);
    });
}

function LibraryResultCard({item, t, onTagClick, isGuest}) {
    const authors = Array.isArray(item?.authors) ? item.authors.filter(Boolean) : [];
    const tags = Array.isArray(item?.tags) ? item.tags.filter(Boolean) : [];
    const sections = Array.isArray(item?.sections) ? item.sections.filter(Boolean) : [];
    const points = Array.isArray(item?.availablePoints) ? item.availablePoints : [];
    const rewardLabel = formatRewardLabel(points, t);
    const priceLabel = formatPriceLabel(item?.priceOptions, t);
    const isFreePrice = !hasPaidPrice(item?.priceOptions);

    return (
        <article className="lib-card">
            <Link className="lib-card__thumb" to={item.url || '/library'}>
                <img src={normalizeUrl(item.imageUrl)} alt={item.title || t('library.title')} />
            </Link>

            <div className="lib-card__body">
                <div className="lib-card__top">
                    <div className="lib-card__pills">
                        {sections.slice(0, 2).map((section) => (
                            <span className="lib-pill" key={`section-${item.id}-${section.id}`}>
                                {section.title}
                            </span>
                        ))}
                        {isGuest && !item.isPublic && (
                            <span className="lib-pill lib-pill--soft">
                                {t('library.section.registrationRequired', 'Registration required')}
                            </span>
                        )}
                    </div>

                    <div className="lib-card__price">
                        <span className={`lib-point lib-point--price ${isFreePrice ? 'lib-point--free-price' : ''}`}>{priceLabel}</span>
                    </div>
                </div>

                <Link className="lib-card__title" to={item.url || '/library'}>
                    {item.title}
                </Link>

                {item.excerpt && <p className="lib-card__excerpt">{item.excerpt}</p>}

                <div className="lib-card__meta">
                    {authors.length > 0 && (
                        <span>
                            {t('library.section.author', 'Author:')} <strong>{authors.join(', ')}</strong>
                        </span>
                    )}
                    {!!item.datePublish && (
                        <span>
                            {t('library.section.datePublish', 'Date published:')} <strong>{item.datePublish}</strong>
                        </span>
                    )}
                    {!!rewardLabel && (
                        <span className="lib-card__reward">
                            <strong>{rewardLabel}</strong>
                        </span>
                    )}
                </div>

                {tags.length > 0 && (
                    <div className="lib-card__tags">
                        {tags.slice(0, 6).map((tag) => (
                            <button
                                className="lib-taglink"
                                key={`${item.id}-${tag}`}
                                type="button"
                                onClick={() => onTagClick(tag)}>
                                #{tag}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </article>
    );
}

export default function LibraryBrowser({
    sectionId = null,
    routePage = 1,
    topAdvertiseUnit = 'library1',
    sidebarAdvertiseUnit = 'library3',
}) {
    const {t} = useTranslation();
    const auth = useAuth();
    const history = useHistory();
    const location = useLocation();
    const allowSectionFilter = !sectionId;
    const hasExplicitSort = useMemo(
        () => new URLSearchParams(location.search || '').has('sort'),
        [location.search]
    );
    const queryState = useMemo(
        () => parseQueryState(location.search, routePage, allowSectionFilter),
        [allowSectionFilter, location.search, routePage]
    );

    const [draft, setDraft] = useState(queryState);
    const [data, setData] = useState({
        items: [],
        pagination: {
            page: 1,
            perPage: PER_PAGE,
            totalItems: 0,
            totalPages: 0,
            hasMore: false,
        },
        filtersMeta: {
            sections: [],
            tags: [],
            authors: [],
        },
        section: null,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [openGroups, setOpenGroups] = useState({
        sections: true,
        tags: true,
        author: true,
        date: true,
    });
    const [tagSearch, setTagSearch] = useState('');

    const pageTitle = data?.section?.title || t('library.title');
    const isGuestView = !auth?.user;

    useMetaTags({
        title: pageTitle,
        description: t('app.description'),
        metas: [{name: 'keywords', content: t('app.keywords')}],
    });

    useEffect(() => {
        setDraft(queryState);
    }, [queryState]);

    useEffect(() => {
        setTagSearch('');
    }, [isFilterOpen]);

    useEffect(() => {
        if (sectionId && !data?.section?.id) {
            return;
        }

        trackPageView(
            sectionId ? `Library: ${pageTitle}` : 'Library',
            'library_view',
            {
                content_group: 'library',
                content_type: sectionId ? 'material_list_section' : 'material_list_global',
                section_id: sectionId || undefined,
                section_name: data?.section?.title || undefined,
            }
        );
    }, [data?.section?.id, data?.section?.title, pageTitle, sectionId]);

    const applyRouteState = useCallback((nextState, replace = false) => {
        const pathname = buildLibraryPath(sectionId, nextState.page || 1);
        const query = buildBrowserQuery(nextState, {
            allowSectionFilter,
            includePage: !sectionId,
        });

        const target = {
            pathname,
            search: query ? `?${query}` : '',
        };

        if (replace) {
            history.replace(target);
            return;
        }

        history.push(target);
    }, [allowSectionFilter, history, sectionId]);

    useEffect(() => {
        const controller = new AbortController();
        setIsLoading(true);

        fetch(`/api/v1/library/materials?${buildApiQuery(queryState, {allowSectionFilter, sectionId})}`, {
            credentials: 'same-origin',
            signal: controller.signal,
        })
            .then(async (response) => {
                const payload = await response.json().catch(() => null);
                return {response, payload};
            })
            .then(({response, payload}) => {
                if (controller.signal.aborted) {
                    return;
                }

                if (response.status === 404 || payload?.state === 'not_found') {
                    setNotFound(true);
                    setData((current) => ({
                        ...current,
                        items: [],
                        pagination: {
                            page: 1,
                            perPage: PER_PAGE,
                            totalItems: 0,
                            totalPages: 0,
                            hasMore: false,
                        },
                    }));
                    return;
                }

                if ((payload?.state === 'closed' || payload?.state === 'promo') && sectionId) {
                    window.location.href = buildLibraryPath(sectionId, queryState.page || 1);
                    return;
                }

                if (!response.ok) {
                    if (payload?.redirectUrl) {
                        history.replace(payload.redirectUrl);
                    }

                    setData((current) => ({
                        ...current,
                        items: [],
                        pagination: {
                            page: 1,
                            perPage: PER_PAGE,
                            totalItems: 0,
                            totalPages: 0,
                            hasMore: false,
                        },
                    }));
                    return;
                }

                setNotFound(false);
                setData({
                    items: Array.isArray(payload?.items) ? payload.items : [],
                    pagination: payload?.pagination || {
                        page: queryState.page,
                        perPage: PER_PAGE,
                        totalItems: 0,
                        totalPages: 0,
                        hasMore: false,
                    },
                    filtersMeta: payload?.filtersMeta || {
                        sections: [],
                        tags: [],
                        authors: [],
                    },
                    section: payload?.section || null,
                });
            })
            .catch((error) => {
                if (error?.name === 'AbortError') {
                    return;
                }

                setData((current) => ({
                    ...current,
                    items: [],
                    pagination: {
                        page: 1,
                        perPage: PER_PAGE,
                        totalItems: 0,
                        totalPages: 0,
                        hasMore: false,
                    },
                }));
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            });

        return () => controller.abort();
    }, [allowSectionFilter, history, queryState, sectionId]);

    const paginationItems = useMemo(
        () => getPaginationItems(data.pagination.page || 1, data.pagination.totalPages || 0),
        [data.pagination.page, data.pagination.totalPages]
    );

    const filteredTagOptions = useMemo(() => {
        const options = Array.isArray(data?.filtersMeta?.tags) ? data.filtersMeta.tags : [];
        const search = tagSearch.trim().toLowerCase();

        if (search === '') {
            return options;
        }

        return options.filter((tag) => String(tag || '').toLowerCase().includes(search));
    }, [data?.filtersMeta?.tags, tagSearch]);

    const sectionMap = useMemo(() => {
        const map = new Map();

        (data?.filtersMeta?.sections || []).forEach((section) => {
            map.set(section.id, section);
        });

        return map;
    }, [data?.filtersMeta?.sections]);

    const authorOptions = Array.isArray(data?.filtersMeta?.authors) ? data.filtersMeta.authors : [];
    const openFilter = useCallback(() => setIsFilterOpen(true), []);
    const closeFilter = useCallback(() => setIsFilterOpen(false), []);
    const filtersLabel = t('search.filters.title', 'Filters');

    const resolveAutoSort = useCallback((state) => {
        if (hasExplicitSort || state.sort !== queryState.sort) {
            return state;
        }

        return {
            ...state,
            sort: (state.search || '').trim() === '' ? 'newest' : 'relevance',
        };
    }, [hasExplicitSort, queryState.sort]);

    const toggleDraftSection = (value) => {
        setDraft((current) => {
            const hasValue = current.sectionIds.includes(value);

            return {
                ...current,
                sectionIds: hasValue
                    ? current.sectionIds.filter((item) => item !== value)
                    : [...current.sectionIds, value],
            };
        });
    };

    const toggleDraftTag = (value) => {
        setDraft((current) => {
            const hasValue = current.tags.includes(value);

            return {
                ...current,
                tags: hasValue
                    ? current.tags.filter((item) => item !== value)
                    : [...current.tags, value],
            };
        });
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        applyRouteState(resolveAutoSort({
            ...draft,
            search: draft.search,
            page: 1,
        }));
    };

    const handleSortChange = (event) => {
        const nextState = {
            ...draft,
            sort: normalizeSortValue(event.target.value),
            page: 1,
        };

        setDraft(nextState);
        applyRouteState(nextState);
    };

    const applyFilters = () => {
        trackEvent('library_filters_apply', {
            content_group: 'library',
            content_type: sectionId ? 'material_list_section' : 'material_list_global',
        });

        applyRouteState(resolveAutoSort({
            ...draft,
            page: 1,
        }));
        closeFilter();
    };

    const resetFilters = () => {
        const nextState = {
            ...draft,
            sort: 'relevance',
            sectionIds: [],
            tags: [],
            authorId: '',
            dateFrom: '',
            dateTo: '',
            page: 1,
        };

        trackEvent('library_filters_reset', {
            content_group: 'library',
            content_type: sectionId ? 'material_list_section' : 'material_list_global',
        });

        setDraft(nextState);
        applyRouteState(nextState);
    };

    const handlePageClick = (nextPage) => {
        if (nextPage < 1 || nextPage === (queryState.page || 1) || nextPage > (data.pagination.totalPages || 1)) {
            return;
        }

        trackEvent('library_pagination_click', {
            content_group: 'library',
            content_type: sectionId ? 'material_list_section' : 'material_list_global',
            page: nextPage,
        });

        applyRouteState({
            ...queryState,
            page: nextPage,
        });
        window.scrollTo({top: 0, behavior: 'smooth'});
    };

    const handleResultTagClick = (tag) => {
        const normalized = String(tag || '').trim().toLowerCase();
        if (normalized === '') {
            return;
        }

        const nextTags = queryState.tags.includes(normalized)
            ? queryState.tags
            : [...queryState.tags, normalized];
        const nextState = {
            ...queryState,
            tags: nextTags,
            page: 1,
        };

        setDraft((current) => ({
            ...current,
            tags: nextTags,
            page: 1,
        }));
        applyRouteState(nextState);
    };

    const removeSectionFilter = (value) => {
        const nextSectionIds = queryState.sectionIds.filter((item) => item !== value);
        const nextState = {
            ...queryState,
            sectionIds: nextSectionIds,
            page: 1,
        };

        setDraft((current) => ({
            ...current,
            sectionIds: nextSectionIds,
            page: 1,
        }));
        applyRouteState(nextState);
    };

    const removeTagFilter = (value) => {
        const nextTags = queryState.tags.filter((item) => item !== value);
        const nextState = {
            ...queryState,
            tags: nextTags,
            page: 1,
        };

        setDraft((current) => ({
            ...current,
            tags: nextTags,
            page: 1,
        }));
        applyRouteState(nextState);
    };

    const clearActiveFilters = () => {
        const nextState = {
            ...queryState,
            sort: 'relevance',
            sectionIds: [],
            tags: [],
            authorId: '',
            dateFrom: '',
            dateTo: '',
            page: 1,
        };

        setDraft(nextState);
        applyRouteState(nextState);
    };

    const toggleFilterGroup = (groupKey) => {
        setOpenGroups((current) => ({
            ...current,
            [groupKey]: !current[groupKey],
        }));
    };

    const renderFilterGroup = (groupKey, title, content) => {
        const isOpen = !!openGroups[groupKey];

        return (
            <div className={`lib-group ${isOpen ? 'is-open' : ''}`}>
                <button
                    className="lib-group__toggle"
                    type="button"
                    onClick={() => toggleFilterGroup(groupKey)}
                    aria-expanded={isOpen}>
                    <span>{title}</span>
                    <span className="lib-group__chevron" aria-hidden="true">
                        {isOpen ? '−' : '+'}
                    </span>
                </button>
                {isOpen && <div className="lib-group__content">{content}</div>}
            </div>
        );
    };

    const renderFilterContent = () => (
        <>
            {allowSectionFilter && renderFilterGroup('sections', t('library.browser.sections', 'Sections'), (
                <>
                    {(data?.filtersMeta?.sections || []).map((section) => (
                        <label className="lib-check" key={`filter-section-${section.id}`}>
                            <input
                                type="checkbox"
                                checked={draft.sectionIds.includes(section.id)}
                                onChange={() => toggleDraftSection(section.id)}
                            />
                            {section.title}
                        </label>
                    ))}
                </>
            ))}

            {renderFilterGroup('tags', t('library.browser.tags', 'Tags'), (
                <>
                    <div className="lib-tagsearch">
                        <span>#</span>
                        <input
                            type="search"
                            value={tagSearch}
                            onChange={(event) => setTagSearch(event.target.value)}
                            placeholder={t('search.inputPlaceholder', 'Search...')}
                        />
                    </div>
                    <div className="lib-tags">
                        {filteredTagOptions.map((tag) => (
                            <button
                                className={`lib-tag ${draft.tags.includes(tag) ? 'is-active' : ''}`}
                                key={`filter-tag-${tag}`}
                                type="button"
                                onClick={() => toggleDraftTag(tag)}>
                                {tag}
                            </button>
                        ))}
                    </div>
                </>
            ))}

            {renderFilterGroup('author', t('library.browser.author', 'Author'), (
                <select
                    className="lib-select"
                    aria-label={t('library.browser.author', 'Author')}
                    id={`library-author-${sectionId || 'all'}`}
                    value={draft.authorId}
                    onChange={(event) => setDraft((current) => ({
                        ...current,
                        authorId: event.target.value,
                    }))}>
                    <option value="">{t('library.browser.allAuthors', 'All authors')}</option>
                    {authorOptions.map((author) => (
                        <option key={`author-${author.id}`} value={String(author.id)}>
                            {author.fullName}
                        </option>
                    ))}
                </select>
            ))}

            {renderFilterGroup('date', t('library.browser.date', 'Date'), (
                <div className="lib-range">
                    <input
                        type="date"
                        value={draft.dateFrom}
                        onChange={(event) => setDraft((current) => ({
                            ...current,
                            dateFrom: event.target.value,
                        }))}
                    />
                    <span>—</span>
                    <input
                        type="date"
                        value={draft.dateTo}
                        onChange={(event) => setDraft((current) => ({
                            ...current,
                            dateTo: event.target.value,
                        }))}
                    />
                </div>
            ))}
        </>
    );

    if (notFound) {
        return <NotFoundPage />;
    }

    return (
        <section className="lib-browser">
            <div className="container">
                <AdvertiseBlock unitId={topAdvertiseUnit} blockLocation="top" />

                {sectionId && (
                    <div className="lib-browser__breadcrumbs">
                        <Link to="/library">{t('library.browser.backToLibrary', 'Back to library')}</Link>
                    </div>
                )}

                <div className="lib-browser__shell">
                    <header className="lib-bar" aria-label={t('search.title')}>
                        <form className="lib-bar__form" onSubmit={handleSearchSubmit} role="search">
                            <div className="lib-bar__search">
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M10.5 3a7.5 7.5 0 105.02 13.08l3.2 3.2a1 1 0 001.41-1.41l-3.2-3.2A7.5 7.5 0 0010.5 3zm0 2a5.5 5.5 0 110 11 5.5 5.5 0 010-11z" fill="currentColor" />
                                </svg>
                                <input
                                    type="search"
                                    value={draft.search}
                                    onChange={(event) => setDraft((current) => ({
                                        ...current,
                                        search: event.target.value,
                                    }))}
                                    placeholder={t('library.browser.searchPlaceholder', 'Search materials')}
                                />
                            </div>

                            <button
                                className="lib-btn lib-btn--ghost lib-bar__filters"
                                type="button"
                                onClick={openFilter}>
                                {filtersLabel}
                            </button>

                            <label className="lib-bar__sort" htmlFor={`library-sort-${sectionId || 'all'}`}>
                                <span>{t('search.filters.sort', 'Sort')}</span>
                                <select
                                    id={`library-sort-${sectionId || 'all'}`}
                                    value={draft.sort}
                                    onChange={handleSortChange}>
                                    <option value="relevance">{t('search.sort.relevance', 'Most relevant')}</option>
                                    <option value="newest">{t('search.sort.newest', 'Newest')}</option>
                                    <option value="popular">{t('surveys.popular', 'Popular')}</option>
                                </select>
                            </label>

                            <button className="lib-btn lib-btn--primary lib-bar__submit" type="submit">
                                {t('search.submit', 'Search')}
                            </button>

                            <div className="lib-bar__meta">
                                <span>{t('search.totalResultsLabel', 'Results:')}</span>
                                <strong>{data.pagination.totalItems || 0}</strong>
                            </div>
                        </form>
                    </header>

                    {(sectionId || queryState.sectionIds.length > 0 || queryState.tags.length > 0 || queryState.authorId || queryState.dateFrom || queryState.dateTo) && (
                        <div className="lib-active">
                            <div className="lib-active__group">
                                {sectionId && data?.section?.title && (
                                    <span className="lib-chip is-static">{data.section.title}</span>
                                )}
                                {!sectionId && queryState.sectionIds.map((selectedSectionId) => {
                                    const section = sectionMap.get(selectedSectionId);
                                    if (!section) {
                                        return null;
                                    }

                                    return (
                                        <button
                                            className="lib-chip is-active"
                                            key={`active-section-${selectedSectionId}`}
                                            type="button"
                                            onClick={() => removeSectionFilter(selectedSectionId)}>
                                            {section.title}
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="lib-active__group">
                                {queryState.tags.map((tag) => (
                                    <button
                                        className="lib-chip is-active"
                                        key={`active-tag-${tag}`}
                                        type="button"
                                        onClick={() => removeTagFilter(tag)}>
                                        #{tag}
                                    </button>
                                ))}

                                <button className="lib-chip" type="button" onClick={clearActiveFilters}>
                                    {t('library.browser.clear', 'Clear filters')}
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="lib-layout">
                        <main className="lib-results" aria-label={t('library.title')}>
                            {isLoading && data.items.length === 0 && (
                                <div className="lib-empty">{t('library.browser.loading', 'Loading...')}</div>
                            )}

                            {!isLoading && data.items.length === 0 && (
                                <div className="lib-empty">
                                    {queryState.search
                                        ? t('library.browser.noResultsFor', {
                                            defaultValue: `No materials found for "${queryState.search}".`,
                                            search: queryState.search,
                                        })
                                        : t('library.browser.noResults', 'No materials found.')}
                                </div>
                            )}

                            {data.items.map((item, index) => (
                                <React.Fragment key={`material-${item.id}`}>
                                    {index === 4 && <AdvertiseBlock unitId={topAdvertiseUnit === 'library1' ? 'library2' : 'libraryPage2'} blockLocation="list" />}
                                    <LibraryResultCard
                                        item={item}
                                        t={t}
                                        onTagClick={handleResultTagClick}
                                        isGuest={isGuestView}
                                    />
                                </React.Fragment>
                            ))}

                            {(data.pagination.totalPages || 0) > 1 && (
                                <div className="lib-pager" aria-label={t('search.paginationAria', 'Pagination')}>
                                    <button
                                        className="lib-btn"
                                        type="button"
                                        disabled={(data.pagination.page || 1) <= 1}
                                        onClick={() => handlePageClick((data.pagination.page || 1) - 1)}>
                                        {t('search.prev', 'Back')}
                                    </button>

                                    {paginationItems.map((item) => {
                                        if (typeof item !== 'number') {
                                            return <span className="lib-pager__ellipsis" key={item}>…</span>;
                                        }

                                        return (
                                            <button
                                                className={`lib-btn ${item === data.pagination.page ? 'is-active' : ''}`}
                                                key={`page-${item}`}
                                                type="button"
                                                onClick={() => handlePageClick(item)}>
                                                {item}
                                            </button>
                                        );
                                    })}

                                    <button
                                        className="lib-btn"
                                        type="button"
                                        disabled={(data.pagination.page || 1) >= (data.pagination.totalPages || 1)}
                                        onClick={() => handlePageClick((data.pagination.page || 1) + 1)}>
                                        {t('search.next', 'Next')}
                                    </button>
                                </div>
                            )}
                        </main>
                    </div>
                </div>
            </div>

            <div className={`lib-drawer-bg ${isFilterOpen ? 'is-open' : ''}`} onClick={closeFilter}></div>
            <aside className={`lib-drawer ${isFilterOpen ? 'is-open' : ''}`} hidden={!isFilterOpen} aria-label={filtersLabel}>
                <div className="lib-drawer__head">
                    <strong>{filtersLabel}</strong>
                    <button className="lib-iconbtn" type="button" onClick={closeFilter} aria-label={t('library.browser.close', 'Close')}>
                        <span>✕</span>
                    </button>
                </div>

                <div className="lib-drawer__body">
                    {renderFilterContent()}
                    {!!sidebarAdvertiseUnit && (
                        <div className="lib-drawer__ad">
                            <AdvertiseBlock unitId={sidebarAdvertiseUnit} blockLocation="sidebar" />
                        </div>
                    )}
                </div>

                <div className="lib-drawer__actions">
                    <button className="lib-btn lib-btn--primary lib-btn--full" type="button" onClick={applyFilters}>
                        {t('search.filters.apply', 'Apply')}
                    </button>
                    <button className="lib-btn lib-btn--ghost lib-btn--full" type="button" onClick={resetFilters}>
                        {t('search.filters.reset', 'Reset')}
                    </button>
                </div>
            </aside>
        </section>
    );
}
